import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import axios from 'axios';

const ProfileHeader = ({ userData, onUpdate, userToken }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [showProModal, setShowProModal] = useState(false);
  const [proUserType, setProUserType] = useState(userData?.type_pro || 'locations_auto_moto');
  const [businessName, setBusinessName] = useState(userData?.nom_pro || '');
  const [businessAddress, setBusinessAddress] = useState(userData?.businessAddress || '');
  const [businessPhone, setBusinessPhone] = useState(userData?.tel_pro || '');
  const [businessEmail, setBusinessEmail] = useState(userData?.businessEmail || '');
  const [businessWebsite, setBusinessWebsite] = useState(userData?.businessWebsite || '');
  const [businessDescription, setBusinessDescription] = useState(userData?.descriptions || '');
  const [businessSpecialties, setBusinessSpecialties] = useState(userData?.specialites || '');
  const [businessHours, setBusinessHours] = useState(userData?.businessHours || '');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localUserData, setLocalUserData] = useState(userData || {});
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  
  const modalRef = useRef();
  const scrollRef = useRef();
  
  const user = localUserData;

  // Types d'activités disponibles
  const activityTypes = [
    { id: 'Service Locations auto/moto', label: t('profileHeader.activityTypes.Service Locations auto/moto'), icon: 'Car' },
    { id: 'Service Lavage auto/moto', label: t('profileHeader.activityTypes.Service Lavage auto/moto'), icon: 'Droplet' },
    { id: 'Service Auto/moto école', label: t('profileHeader.activityTypes.Service Auto/moto école'), icon: 'GraduationCap' },
    { id: 'Service Garage/mécanique', label: t('profileHeader.activityTypes.Service Garage/mécanique'), icon: 'Settings' },
    { id: 'Service Visite technique', label: t('profileHeader.activityTypes.Service Visite technique'), icon: 'CheckCircle' },
    { id: 'Service Entretien (vidange/huile)', label: t('profileHeader.activityTypes.Service Entretien (vidange/huile)'), icon: 'Tool' },
    { id: 'Service Carrosserie/Peinture', label: t('profileHeader.activityTypes.Service Carrosserie/Peinture'), icon: 'Palette' },
    { id: 'Service Assurance auto/moto', label: t('profileHeader.activityTypes.Service Assurance auto/moto'), icon: 'Shield' },
    { id: 'Service Vente auto/moto', label: t('profileHeader.activityTypes.Service Vente auto/moto'), icon: 'DollarSign' },
    { id: 'Service Vente de pièces', label: t('profileHeader.activityTypes.Service Vente de pièces'), icon: 'Package' }
  ];

  // Configuration d'Axios
  const axiosInstance = axios.create({
    baseURL: 'https://api.autocasses.com/api',
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    }
  });

  // Intercepteur pour ajouter le token
  axiosInstance.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem('userToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Fonction utilitaire pour extraire les messages d'erreur
  const extractErrorMessage = (error) => {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message === 'Network Error') {
      return t('profileHeader.messages.networkError');
    }
    if (error.code === 'ECONNABORTED') {
      return t('profileHeader.messages.timeoutError');
    }
    return t('profileHeader.messages.error');
  };

  // Initialiser les champs
  useEffect(() => {
    if (localUserData) {
      setProUserType(localUserData.type_pro || 'locations_auto_moto');
      setBusinessName(localUserData.nom_pro || '');
      setBusinessAddress(localUserData.businessAddress || '');
      setBusinessPhone(localUserData.tel_pro || '');
      setBusinessEmail(localUserData.businessEmail || '');
      setBusinessWebsite(localUserData.businessWebsite || '');
      setBusinessDescription(localUserData.descriptions || '');
      setBusinessSpecialties(localUserData.specialites || '');
      setBusinessHours(localUserData.businessHours || '');
    }
  }, [localUserData]);

  // Mettre à jour les données locales
  useEffect(() => {
    if (userData) {
      setLocalUserData(userData);
    }
  }, [userData]);

  // Gestion du clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showProModal) {
        closeProModal();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showProModal]);

  // Rafraîchir les données
  const refreshUserData = useCallback(async () => {
    try {
      setLoading(true);
      if (onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      console.error('Erreur lors du rafraîchissement:', error);
    } finally {
      setLoading(false);
    }
  }, [onUpdate]);

  // Valider le formulaire
  const validateForm = () => {
    if (!businessName.trim()) {
      alert(t('profileHeader.validation.companyRequired'));
      return false;
    }
    if (!businessAddress.trim()) {
      alert(t('profileHeader.validation.addressRequired'));
      return false;
    }
    if (!businessPhone.trim()) {
      alert(t('profileHeader.validation.phoneRequired'));
      return false;
    }
    return true;
  };

  // Activer/mettre à jour le profil pro
  const handleActivateProProfile = useCallback(async () => {
    if (!validateForm() || submitting) {
      return;
    }

    setSubmitting(true);
    
    try {
      const proData = {
        type_pro: proUserType,
        nom_pro: businessName,
        tel_pro: businessPhone,
        descriptions: businessDescription,
        specialites: businessSpecialties,
        businessAddress: businessAddress,
        businessEmail: businessEmail,
        businessWebsite: businessWebsite,
        businessHours: businessHours
      };

      const token = localStorage.getItem('userToken');
      if (!token) {
        throw new Error('Token non disponible');
      }

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 15000
      };

      const apiUrl = user?.isPro ? '/user/update-pro' : '/user/activate-pro';
      const method = user?.isPro ? 'put' : 'post';
      
      const response = await axiosInstance[method](apiUrl, proData, config);

      if (response.status === 200 || response.status === 201) {
        // Rafraîchir les données
        await refreshUserData();
        
        // Fermer la modal
        setShowProModal(false);
        
        // Afficher message de succès
        const successMessage = user?.isPro 
          ? t('profileHeader.messages.updateSuccess')
          : t('profileHeader.messages.activationSuccess');
        
        alert(successMessage);
      }
    } catch (error) {
      console.error('Erreur API:', error);
      const errorMessage = extractErrorMessage(error);
      alert(`${t('profileHeader.messages.error')}: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  }, [submitting, businessName, businessAddress, businessPhone, user?.isPro, proUserType, businessDescription, businessSpecialties, businessEmail, businessWebsite, businessHours, refreshUserData]);

  // Ouvrir la modal
  const openProModal = useCallback(async () => {
    const token = localStorage.getItem('userToken');
    if (!token) {
      navigate('/auth');
      return;
    }
    setShowProModal(true);
    document.body.style.overflow = 'hidden'; // Empêcher le scroll
  }, [navigate]);

  // Fermer la modal
  const closeProModal = useCallback(() => {
    if (!submitting) {
      setShowProModal(false);
      document.body.style.overflow = 'auto'; // Réactiver le scroll
    }
  }, [submitting]);

  // Badge de vérification
  const getVerificationBadge = () => {
    if (user?.isPro) {
      return {
        icon: 'ShieldCheck',
        text: t('profileHeader.status.pro'),
        color: 'text-green-600',
        bgColor: 'bg-green-50 border-green-200'
      };
    } else if (user?.isVerified) {
      return {
        icon: 'CheckCircle',
        text: t('profileHeader.status.verified'),
        color: 'text-blue-600',
        bgColor: 'bg-blue-50 border-blue-200'
      };
    }
    return null;
  };

  const getTypeLabel = (type) => {
    const activity = activityTypes.find(a => a.id === type);
    return activity ? activity.label : t('profileHeader.defaults.autoProfessional');
  };

  const getTypeIcon = (type) => {
    const activity = activityTypes.find(a => a.id === type);
    return activity ? activity.icon : 'Briefcase';
  };

  // Gérer les actions de contact
  const handleContactAction = (type, value) => {
    if (!value) return;
    
    switch(type) {
      case 'phone':
        window.location.href = `tel:${value}`;
        break;
      case 'email':
        window.location.href = `mailto:${value}`;
        break;
      case 'website':
        let url = value;
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }
        window.open(url, '_blank');
        break;
      case 'address':
        const encodedAddress = encodeURIComponent(value);
        window.open(`https://maps.google.com/?q=${encodedAddress}`, '_blank');
        break;
    }
  };

  // Rendu de la modal (comme dans votre fichier précédent)
  const renderProModal = () => {
    if (!showProModal) return null;

    return (
      <div 
        className="fixed inset-0 z-50 bg-black bg-opacity-40 animate-fade-in flex items-end md:items-center justify-center"
        onClick={closeProModal}
      >
        <div 
          ref={modalRef}
          className="bg-white rounded-t-xl md:rounded-xl shadow-xl animate-slide-up md:animate-slide-up-sm w-full max-w-2xl max-h-[85vh] md:max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Handle Bar pour mobile */}
          <div className="md:hidden w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-4"></div>
          
          {/* En-tête de la modal */}
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                user?.isPro ? 'bg-green-100' : 'bg-blue-100'
              }`}>
                <Icon 
                  name={user?.isPro ? "Briefcase" : "Rocket"} 
                  size={24} 
                  className={user?.isPro ? "text-green-600" : "text-blue-600"} 
                />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {user?.isPro ? t('profileHeader.modal.editTitle') : t('profileHeader.modal.becomeTitle')}
                </h2>
                <p className="text-sm text-gray-600">
                  {user?.isPro ? t('profileHeader.modal.editSubtitle') : t('profileHeader.modal.becomeSubtitle')}
                </p>
              </div>
            </div>
            <button
              onClick={closeProModal}
              disabled={submitting}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <Icon name="X" size={20} className="text-gray-600" />
            </button>
          </div>

          {/* Contenu de la modal */}
          <div 
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-6 py-4"
          >
            {/* Section Type d'activité */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <Icon name="Grid" size={20} className="text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">{t('profileHeader.modal.activityType')}</h3>
              </div>
              <p className="text-gray-600 mb-4 text-sm">{t('profileHeader.modal.activityDescription')}</p>
              
              <div className="flex overflow-x-auto gap-3 pb-4 -mx-6 px-6">
                {activityTypes.map((activity) => (
                  <button
                    key={activity.id}
                    onClick={() => setProUserType(activity.id)}
                    disabled={submitting}
                    className={`flex-shrink-0 w-40 p-4 rounded-xl border-2 transition-all ${
                      proUserType === activity.id
                        ? 'border-blue-500 bg-blue-50 shadow-sm'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${
                      proUserType === activity.id ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <Icon 
                        name={activity.icon} 
                        size={24} 
                        className={proUserType === activity.id ? 'text-blue-600' : 'text-gray-600'} 
                      />
                    </div>
                    <span className={`text-sm font-medium text-center block ${
                      proUserType === activity.id ? 'text-blue-700' : 'text-gray-700'
                    }`}>
                      {activity.label}
                    </span>
                    {proUserType === activity.id && (
                      <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center border-2 border-white">
                        <Icon name="Check" size={14} className="text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Informations de base */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="Info" size={20} className="text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">{t('profileHeader.modal.basicInfo')}</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('profileHeader.modal.companyName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder={t('profileHeader.modal.companyName')}
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    disabled={submitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('profileHeader.modal.address')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder={t('profileHeader.modal.address')}
                    value={businessAddress}
                    onChange={(e) => setBusinessAddress(e.target.value)}
                    disabled={submitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('profileHeader.modal.phone')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    placeholder={t('profileHeader.modal.phone')}
                    value={businessPhone}
                    onChange={(e) => setBusinessPhone(e.target.value)}
                    disabled={submitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('profileHeader.modal.email')}
                  </label>
                  <input
                    type="email"
                    placeholder={t('profileHeader.modal.email')}
                    value={businessEmail}
                    onChange={(e) => setBusinessEmail(e.target.value)}
                    disabled={submitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('profileHeader.modal.website')}
                  </label>
                  <input
                    type="url"
                    placeholder={t('profileHeader.modal.website')}
                    value={businessWebsite}
                    onChange={(e) => setBusinessWebsite(e.target.value)}
                    disabled={submitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="FileText" size={20} className="text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">{t('profileHeader.modal.presentation')}</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('profileHeader.modal.description')}
                  </label>
                  <textarea
                    placeholder={t('profileHeader.modal.descriptionPlaceholder')}
                    value={businessDescription}
                    onChange={(e) => setBusinessDescription(e.target.value)}
                    disabled={submitting}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-100"
                    maxLength={500}
                  />
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {businessDescription.length}/500 {t('profileHeader.modal.charCount')}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('profileHeader.modal.specialties')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('profileHeader.modal.specialtiesPlaceholder')}
                    value={businessSpecialties}
                    onChange={(e) => setBusinessSpecialties(e.target.value)}
                    disabled={submitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('profileHeader.modal.hours')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('profileHeader.modal.hoursPlaceholder')}
                    value={businessHours}
                    onChange={(e) => setBusinessHours(e.target.value)}
                    disabled={submitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* Note informative */}
            {user?.isPro && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    {t('profileHeader.modal.infoNote')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Actions de la modal */}
          <div className="flex gap-3 p-6 border-t border-gray-200">
            <button
              onClick={closeProModal}
              disabled={submitting}
              className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {t('profileHeader.buttons.cancel')}
            </button>
            <button
              onClick={handleActivateProProfile}
              disabled={submitting}
              className="flex-1 px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:bg-blue-400 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  {t('profileHeader.buttons.processing')}
                </>
              ) : (
                <>
                  <Icon 
                    name={user?.isPro ? "Save" : "Rocket"} 
                    size={18} 
                  />
                  {user?.isPro ? t('profileHeader.buttons.save') : t('profileHeader.buttons.activate')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
      {/* Badge de statut */}
      {(() => {
        const badge = getVerificationBadge();
        if (badge) {
          return (
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6 ${badge.bgColor}`}>
              <Icon name={badge.icon} size={18} className={badge.color} />
              <span className={`text-sm font-semibold ${badge.color}`}>
                {badge.text}
              </span>
            </div>
          );
        }
        return null;
      })()}

      {/* Contenu basé sur le type de compte */}
      {user?.isPro ? (
        <div className="space-y-8">
          {/* En-tête du profil pro */}
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-blue-50 border-4 border-blue-100 flex items-center justify-center">
              <Icon 
                name={getTypeIcon(user.type_pro) || "Briefcase"} 
                size={36} 
                className="text-blue-600" 
              />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{user.nom_pro}</h1>
              <div className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full">
                <Icon name="Briefcase" size={14} />
                <span className="text-sm font-semibold">{getTypeLabel(user.type_pro)}</span>
              </div>
            </div>
          </div>

          {/* Grille de contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {user.tel_pro && (
              <button 
                onClick={() => handleContactAction('phone', user.tel_pro)}
                className="group p-4 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-sm transition-all text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                    <Icon name="Phone" size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      {t('profileHeader.contact.call')}
                    </p>
                    <p className="font-medium text-gray-900">{user.tel_pro}</p>
                  </div>
                </div>
              </button>
            )}
            
            {user.businessAddress && (
              <button 
                onClick={() => handleContactAction('address', user.businessAddress)}
                className="group p-4 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-sm transition-all text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center group-hover:bg-green-100 transition-colors">
                    <Icon name="MapPin" size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      {t('profileHeader.contact.locate')}
                    </p>
                    <p className="font-medium text-gray-900 line-clamp-2">{user.businessAddress}</p>
                  </div>
                </div>
              </button>
            )}
            
            {user.businessEmail && (
              <button 
                onClick={() => handleContactAction('email', user.businessEmail)}
                className="group p-4 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-sm transition-all text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-yellow-50 flex items-center justify-center group-hover:bg-yellow-100 transition-colors">
                    <Icon name="Mail" size={20} className="text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      {t('profileHeader.contact.email')}
                    </p>
                    <p className="font-medium text-gray-900 line-clamp-1">{user.businessEmail}</p>
                  </div>
                </div>
              </button>
            )}
            
            {user.businessWebsite && (
              <button 
                onClick={() => handleContactAction('website', user.businessWebsite)}
                className="group p-4 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-sm transition-all text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 transition-colors">
                    <Icon name="Globe" size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      {t('profileHeader.contact.website')}
                    </p>
                    <p className="font-medium text-gray-900 line-clamp-1">{user.businessWebsite}</p>
                  </div>
                </div>
              </button>
            )}
          </div>

          {/* Sections d'information */}
          {user.descriptions && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Icon name="FileText" size={20} className="text-gray-700" />
                <h2 className="text-xl font-semibold text-gray-900">{t('profileHeader.infoSections.about')}</h2>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{user.descriptions}</p>
              </div>
            </div>
          )}

          {user.specialites && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Icon name="Award" size={20} className="text-gray-700" />
                <h2 className="text-xl font-semibold text-gray-900">{t('profileHeader.infoSections.specialties')}</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {user.specialites.split(',').map((specialty, index) => (
                  specialty.trim() && (
                    <div key={index} className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full">
                      <Icon name="CheckCircle" size={14} />
                      <span className="font-medium">{specialty.trim()}</span>
                    </div>
                  )
                ))}
              </div>
            </div>
          )}

          {user.businessHours && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Icon name="Clock" size={20} className="text-gray-700" />
                <h2 className="text-xl font-semibold text-gray-900">{t('profileHeader.infoSections.hours')}</h2>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-gray-700 font-medium">{user.businessHours}</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {/* Profil personnel */}
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center">
              <Icon name="User" size={36} className="text-gray-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {t('profileHeader.welcome', { name: user?.nom ? `, ${user.nom}` : '' })}
              </h1>
              <p className="text-gray-600 text-lg">{t('profileHeader.personalTitle')}</p>
            </div>
          </div>

          {/* Carte de promotion pro */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-6">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-sm">
                <Icon name="Rocket" size={28} className="text-orange-500" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-orange-900 mb-3">{t('profileHeader.proCard.title')}</h2>
                <p className="text-orange-800 mb-6 leading-relaxed">
                  {t('profileHeader.proCard.description')}
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Icon name="CheckCircle" size={18} className="text-green-600" />
                    <span className="font-medium text-orange-900">{t('profileHeader.proCard.benefits.verified')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Icon name="CheckCircle" size={18} className="text-green-600" />
                    <span className="font-medium text-orange-900">{t('profileHeader.proCard.benefits.visibility')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Icon name="CheckCircle" size={18} className="text-green-600" />
                    <span className="font-medium text-orange-900">{t('profileHeader.proCard.benefits.tools')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bouton d'action principal */}
      <div className="mt-8 pt-8 border-t border-gray-200">
        <button
          onClick={openProModal}
          className={`w-full flex items-center justify-between px-6 py-4 rounded-xl transition-all duration-300 ${
            user?.isPro 
              ? 'bg-green-600 hover:bg-green-700 shadow-lg hover:shadow-xl' 
              : 'bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl'
          }`}
        >
          <div className="flex items-center gap-3">
            <Icon 
              name={user?.isPro ? "Edit" : "Sparkles"} 
              size={22} 
              className="text-white" 
            />
            <span className="text-lg font-semibold text-white">
              {user?.isPro ? t('profileHeader.buttons.editPro') : t('profileHeader.buttons.becomePro')}
            </span>
          </div>
          <Icon name="ArrowRight" size={20} className="text-white" />
        </button>
      </div>

      {/* Modal */}
      {renderProModal()}

      {/* Styles CSS pour les animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        
        @keyframes slideUp-sm {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
        
        .animate-slide-up-sm {
          animation: slideUp-sm 0.3s ease-out;
        }
        
        .line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
        }
        
        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        
        .whitespace-pre-line {
          white-space: pre-line;
        }
      `}</style>
    </div>
  );
};

export default ProfileHeader;